import { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Card, Typography } from "@mui/material";
import { Layout } from "../Layout";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppHeader } from "JS/React/Components/AppHeader";
import sortableHeader from "JS/React/HOC/SortableHeader";
import { ColumnSort, getColumnSort, sortData, Sorting } from "JS/Types";
import { GiftCategories } from ".";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AllTabs } from "JS/Models";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import qs from "qs";
import { useGiftsGiven } from "JS/React/Hooks/Gifts";

const SortableHeader = sortableHeader<HTMLSpanElement>("span");

const RootDiv = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    color: theme.palette.grey[500],
}));

const ItemCard = styled(Card)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 15px",
    color: theme.palette.grey[500],
    "&:hover": {
        cursor: "pointer",
    },
}));

const FlexBox = styled(Box)(() => ({
    display: "flex",
}));

const AlignCenterBox = styled(Box)(() => ({
    display: "flex",
    alignItems: "center",
}));

const MemberIdTypography = styled(Typography)(() => ({
    marginRight: "10px",
}));

const NoContentTypography = styled(Typography)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    color: `${theme.palette.common.black}`,
}));

export const GiftsGivenListing = (props: any) => {
    const params: any = useParams();
    const { categoryId } = params;
    const Tabs: any = AllTabs;
    const { className, ...rest } = props;
    const { homeStack } = useGlobalNavStack();
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const itemRefs = useRef([]);
    const { scrollTo } = useQueryParams();
    const {
        setGlobalGiftsCategoryStack,
        resetGlobalGiftsCategoryStack,
        giftsStack,
    } = useGlobalNavStack();
    const [searchQuery, setSearchQuery] = useState<string>(
        giftsStack[categoryId]?.searchQuery,
    );
    const { loading, loaded, filteredMembers } = useGiftsGiven(
        "members",
        searchQuery,
    );
    const [sorting, setSorting] = useState<Sorting>(
        giftsStack[categoryId]?.sorting,
    );

    useEffect(() => {
        if (
            !loading &&
            finalData?.length &&
            +scrollTo >= 0 &&
            itemRefs?.current[+scrollTo]
        ) {
            itemRefs.current[+scrollTo - 6]?.scrollIntoView({
                behavior: "instant",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollTo, loading]);
    const onSortChange = (columnSort: ColumnSort, oldOrder: number) => {
        setSorting([columnSort]);
        setGlobalGiftsCategoryStack(
            {
                sorting: [columnSort],
            },
            categoryId,
        );
    };

    const finalData = useMemo(() => {
        return sortData(filteredMembers, sorting);
    }, [filteredMembers, sorting]);

    useEffect(() => {
        setGlobalGiftsCategoryStack(
            {
                searchQuery: searchQuery,
            },
            categoryId,
        );
    }, [filteredMembers]);

    useEffect(() => {
        if (giftsStack[categoryId]?.scrollPosition === 0) {
            window.scrollTo(0, 500);
            window.scrollTo(0, 0);
        }
    }, []);

    return (
        <Layout>
            <RootDiv className={className} {...rest}>
                <AppHeader
                    title={"Gifts Given"}
                    canGoBack
                    searchable={categoryId !== GiftCategories.SEND_MEMBER}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchPlaceholder={`Search Members by Name, LTD ID`}
                    onBackClick={() => {
                        if (!homeStack?.isHomeTrack) {
                            resetGlobalGiftsCategoryStack(categoryId);
                            resetGlobalGiftsCategoryStack(
                                `${categoryId}-content`,
                            );
                            navigate(linkProvider.react.gifts().index());
                        } else navigate(linkProvider.react.home(), { replace: true });
                    }}
                />

                <Box
                    display={"flex"}
                    justifyContent={"space-around"}
                    marginBottom={"22px"}
                >
                    <SortableHeader
                        upDirection="asc"
                        downDirection="desc"
                        sort={getColumnSort(sorting, {
                            col: "first_name",
                            position: sorting.length,
                        })}
                        onSortChanged={onSortChange}
                    >
                        <span>A-Z</span>
                    </SortableHeader>
                    <SortableHeader
                        upDirection="asc"
                        downDirection="desc"
                        sort={getColumnSort(sorting, {
                            col: "member_id",
                            position: sorting.length,
                        })}
                        onSortChanged={onSortChange}
                    >
                        <span>LTD ID</span>
                    </SortableHeader>
                </Box>

                {loaded &&
                    finalData?.map((u, idx) => (
                        <ItemCard
                            key={idx}
                            elevation={0}
                            ref={(el) => { itemRefs.current[idx] = el; }}
                            onClick={() => {
                                navigate(
                                    linkProvider.react
                                        .gifts()
                                        .giftsGivenContentListing(
                                            u?.member_id,
                                            Tabs.AUDIOS,
                                            `${categoryId}-content`,
                                            { scrollTo: idx },
                                        ),
                                );
                            }}
                        >
                            <FlexBox>
                                <Typography>
                                    {u.first_name || ""} {u.last_name || ""}
                                </Typography>
                            </FlexBox>
                            <AlignCenterBox>
                                <MemberIdTypography>
                                    {u.member_id || ""}
                                </MemberIdTypography>
                                <FontAwesomeIcon
                                    icon={["fas", "chevron-right"]}
                                />
                            </AlignCenterBox>
                        </ItemCard>
                    ))}

                {loading && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging.loader.content}
                    />
                )}

                {loaded && !filteredMembers.length && (
                    <NoContentTypography align="center" variant="h6">
                        {messaging.common.noItemsFound}
                    </NoContentTypography>
                )}
            </RootDiv>
        </Layout>
    );
};

const useQueryParams = () => {
    const location = useLocation();
    return useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });
        const { scrollTo = "0" } = parsedQueryString;

        return {
            scrollTo,
        };
    }, [location?.search]);
};
